'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Component, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import { requestFailedOrUninitialized, requestFailed, requestSucceeded } from 'redux-request-state/util/requestStateCheckers';
import PortalIdParser from 'PortalIdParser';
import { Button } from 'foundations-components/transitional/components/Button';
import { MultiColumnGroup, MultiColumnItem } from 'foundations-components/transitional/components/MultiColumn';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getContentModelId, getContentModelIsPublished } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getContentModelTitle } from '../redux/selectors/postContentModelSelectors';
import { getHasSocialAccessScope } from '../redux/selectors/authSelectors';
import { getSocialChannelsEnabledForBlog, getPortalHasSocialChannels, getBlogPublishToSocialMediaTaskStatus, getSuccessfulSocialAutoPublishDate } from '../redux/selectors/postSettingsSelectors';
import { getSocialAutoPublishStatusRequest, getSocialBroadcastsRequest } from '../redux/selectors/postRequestStatesSelectors';
import { fetchSocialBroadcasts } from '../redux/actions/postSettingsActions';
import { openSocialComposer } from '../redux/actions/socialComposerActions';
import { fetchPostBlogs } from '../redux/actions/postSettingsActions';
import { getPostBlogsRequest } from '../redux/selectors/postRequestStatesSelectors';
import { getEditableSlugRequest } from 'ContentEditorUI/redux/selectors/requestStatesSelectors';
import { fetchEditableSlugInfo } from 'ContentEditorUI/redux/actions/slugInfoActions';
import { getPostTitlePlaceholder } from 'ContentEditorUI/utils/helpers';
import { useCurrentContentContext } from 'ContentEditorUI/utils/contentContextUtils';
import { getPublishSocialComposerParams } from '../redux/selectors/socialComposerSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OpenSocialComposerButton = ({
  disabled
}) => {
  const socialComposerOptions = useSelector(getPublishSocialComposerParams);
  const dispatch = useDispatch();
  const onSuccess = useCallback(postBody => dispatch(openSocialComposer({
    blogPost: Object.assign({}, socialComposerOptions, {
      postBody
    })
  })), [dispatch, socialComposerOptions]);
  const {
    getCurrentContentContext: getPostBodyValueAndOpenSocialComposer
  } = useCurrentContentContext('#hs_cos_wrapper_post_body', onSuccess);
  return /*#__PURE__*/_jsx(Button, {
    onClick: getPostBodyValueAndOpenSocialComposer,
    block: true,
    use: "tertiary-light",
    size: "small",
    disabled: disabled,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: getLangKey('previewButton')
    })
  });
};
function getLangKey(key) {
  return `socialAutoPublishing.${key}`;
}
function getSocialLink() {
  return `/social/${PortalIdParser.get()}`;
}
function getSocialSettingsLink() {
  return `${getSocialLink()}/settings`;
}
class SocialAutoPublishingContainer extends Component {
  componentDidMount() {
    const {
      editableSlugRequest,
      postId,
      socialBroadcastsRequest,
      postBlogsRequest
    } = this.props;
    if (requestFailedOrUninitialized(socialBroadcastsRequest)) {
      this.props.fetchSocialBroadcasts(postId);
    }
    if (requestFailed(postBlogsRequest)) {
      this.props.fetchPostBlogs();
    }
    if (requestFailedOrUninitialized(editableSlugRequest)) {
      this.props.fetchEditableSlugInfo(postId);
    }
  }
  getDescriptionLangKey() {
    const {
      socialChannelsEnabledForBlog,
      portalHasSocialChannels
    } = this.props;
    let key = 'enabledForBlog';
    if (portalHasSocialChannels && !socialChannelsEnabledForBlog) {
      key = 'notEnabledForBlog';
    }
    return getLangKey(`description.${key}`);
  }
  renderBapCustomizeButton() {
    const {
      canCustomizeSocial,
      title
    } = this.props;
    const titleIsPlaceholder = getPostTitlePlaceholder() === title;
    const buttonEl = /*#__PURE__*/_jsx(OpenSocialComposerButton, {
      disabled: !canCustomizeSocial || titleIsPlaceholder
    });
    if (!canCustomizeSocial || titleIsPlaceholder) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getLangKey(!canCustomizeSocial ? 'canCustomizeSocialTooltip' : 'titleIsPlaceholder')
        }),
        children: buttonEl
      });
    }
    return buttonEl;
  }
  renderEnableButton() {
    return /*#__PURE__*/_jsx(Button, {
      href: getSocialSettingsLink(),
      block: true,
      use: "tertiary-light",
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getLangKey('enableButton')
      })
    });
  }
  renderPublishedMessage() {
    const {
      blogPublishToSocialMediaTaskStatus,
      successfulSocialAutoPublishDate
    } = this.props;
    let publishDate;
    let langKey = getLangKey('description.published');
    if (!blogPublishToSocialMediaTaskStatus) {
      langKey = `${langKey}.socialWontPublish`;
    } else if (blogPublishToSocialMediaTaskStatus === 'DONE') {
      if (successfulSocialAutoPublishDate) {
        publishDate = I18n.moment.utc(successfulSocialAutoPublishDate).portalTz().format('LLL');
        langKey = `${langKey}.socialPublished`;
      } else {
        langKey = `${langKey}.socialPublishedWithoutDate`;
      }
    } else {
      langKey = `${langKey}.socialProcessing`;
    }
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: langKey,
      options: {
        publishDate,
        socialHref: getSocialLink()
      }
    });
  }
  renderContent() {
    const {
      socialChannelsEnabledForBlog,
      portalHasSocialChannels,
      postIsPublished
    } = this.props;
    if (postIsPublished && portalHasSocialChannels && socialChannelsEnabledForBlog) {
      return this.renderPublishedMessage();
    }
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: this.getDescriptionLangKey(),
      options: {
        socialSettingsHref: getSocialSettingsLink()
      }
    });
  }
  renderAction() {
    const {
      blogPublishToSocialMediaTaskStatus,
      socialChannelsEnabledForBlog,
      portalHasSocialChannels,
      postIsPublished
    } = this.props;
    if (portalHasSocialChannels && !socialChannelsEnabledForBlog) {
      return this.renderEnableButton();
    } else if (postIsPublished) {
      if (blogPublishToSocialMediaTaskStatus === 'DONE') {
        return /*#__PURE__*/_jsx(Tag, {
          use: "success",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "publish.panel.states.published"
          })
        });
      }
      return null;
    }
    return this.renderBapCustomizeButton();
  }
  render() {
    const {
      socialChannelsEnabledForBlog,
      portalHasSocialChannels,
      hasSocialAccessScope,
      socialAutoPublishStatusRequest,
      socialBroadcastsRequest,
      postBlogsRequest
    } = this.props;
    if (!requestSucceeded(socialAutoPublishStatusRequest) || !requestSucceeded(socialBroadcastsRequest) || !requestSucceeded(postBlogsRequest) || !hasSocialAccessScope || !socialChannelsEnabledForBlog && !portalHasSocialChannels) {
      return null;
    }
    return /*#__PURE__*/_jsxs(MultiColumnGroup, {
      children: [/*#__PURE__*/_jsx(MultiColumnItem, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "socialAutoPublishing.title"
        }),
        children: this.renderContent()
      }), /*#__PURE__*/_jsx(MultiColumnItem, {
        children: this.renderAction()
      })]
    });
  }
}
SocialAutoPublishingContainer.propTypes = {
  hasSocialAccessScope: PropTypes.bool,
  postId: PropTypes.number,
  postIsPublished: PropTypes.bool,
  socialChannelsEnabledForBlog: PropTypes.bool,
  portalHasSocialChannels: PropTypes.bool,
  title: PropTypes.string,
  socialAutoPublishStatusRequest: PropTypes.object.isRequired,
  blogPublishToSocialMediaTaskStatus: PropTypes.string,
  fetchSocialBroadcasts: PropTypes.func.isRequired,
  socialBroadcastsRequest: PropTypes.object.isRequired,
  successfulSocialAutoPublishDate: PropTypes.number,
  canCustomizeSocial: PropTypes.bool,
  fetchPostBlogs: PropTypes.func.isRequired,
  postBlogsRequest: PropTypes.object.isRequired,
  editableSlugRequest: PropTypes.object.isRequired,
  fetchEditableSlugInfo: PropTypes.func.isRequired
};
const mapStateToProps = state => {
  return {
    hasSocialAccessScope: getHasSocialAccessScope(state),
    postId: getContentModelId(state),
    postIsPublished: getContentModelIsPublished(state),
    title: getContentModelTitle(state),
    blogPublishToSocialMediaTaskStatus: getBlogPublishToSocialMediaTaskStatus(state),
    socialAutoPublishStatusRequest: getSocialAutoPublishStatusRequest(state),
    socialBroadcastsRequest: getSocialBroadcastsRequest(state),
    socialChannelsEnabledForBlog: getSocialChannelsEnabledForBlog(state),
    successfulSocialAutoPublishDate: getSuccessfulSocialAutoPublishDate(state),
    portalHasSocialChannels: getPortalHasSocialChannels(state),
    postBlogsRequest: getPostBlogsRequest(state),
    editableSlugRequest: getEditableSlugRequest(state)
  };
};
const mapDispatchToProps = {
  fetchEditableSlugInfo,
  fetchSocialBroadcasts,
  fetchPostBlogs
};
export default connect(mapStateToProps, mapDispatchToProps)(SocialAutoPublishingContainer);